import React, { useState } from 'react';
import { LoadingButton, SimpleButton, useHttp } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';

const STATO_CONFIG = {
  rispettata: { border: 'border-green-400',  bg: 'bg-green-50',   badge: 'bg-green-100 text-green-700',   labelKey: 'ppp_scadenza_rispettata' },
  scaduta:    { border: 'border-red-400',    bg: 'bg-red-50',     badge: 'bg-red-100 text-red-700',       labelKey: 'ppp_scadenza_scaduta' },
  critica:    { border: 'border-red-300',    bg: 'bg-red-50',     badge: 'bg-red-100 text-red-700',       labelKey: null },
  a_rischio:  { border: 'border-orange-300', bg: 'bg-orange-50',  badge: 'bg-orange-100 text-orange-700', labelKey: null },
  in_tempo:   { border: 'border-green-200',  bg: 'bg-green-50',   badge: 'bg-green-100 text-green-700',  labelKey: null },
};

function fmt(dateStr) {
  return dateStr ? new Date(dateStr).toLocaleDateString('it-IT') : '—';
}

const ALLEGATO_ACCEPT = '.pdf,.doc,.docx,.odt,.png,.jpg,.jpeg';

function ProrogaModal({ scadenza, procedimentoId, onClose, onSuccess, _locate }) {
  const api = usePppApi();
  const [nuovaData, setNuovaData] = useState('');
  const [motivazione, setMotivazione] = useState('');
  const [allegato, setAllegato] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dataMin = new Date();
  dataMin.setDate(dataMin.getDate() + 1);
  const dataMinStr = dataMin.toISOString().split('T')[0];

  const handleSubmit = () => {
    if (!nuovaData || !motivazione || motivazione.length < 10) {
      setError(_locate('ppp_proroga_errore_validazione'));
      return;
    }
    setSaving(true);
    setError(null);
    api.prorogaScadenza(procedimentoId, scadenza.id, { nuovaData, motivazione, allegato }, {
      onSuccess: () => { setSaving(false); onSuccess(); onClose(); },
      onFormError: (e) => { setSaving(false); setError(e?.message ?? _locate('ppp_errore_generico')); },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">{_locate('ppp_proroga_titolo')}</h3>
        <p className="text-sm text-gray-500 mb-4">{scadenza.descrizione}</p>

        <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_proroga_nuova_data')}</label>
        <input
          type="date" min={dataMinStr} value={nuovaData}
          onChange={(e) => setNuovaData(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_proroga_motivazione')}</label>
        <textarea
          rows={4} value={motivazione}
          onChange={(e) => setMotivazione(e.target.value)}
          placeholder={_locate('ppp_proroga_motivazione_placeholder')}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
        <p className="text-xs text-gray-400 mb-4">{motivazione.length}/1000 — min. 10 caratteri</p>

        <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_proroga_allegato_label')}</label>
        <input
          type="file" accept={ALLEGATO_ACCEPT}
          onChange={(e) => setAllegato(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-1"
        />
        <p className="text-xs text-gray-400 mb-4">{_locate('ppp_proroga_allegato_hint')}</p>

        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <SimpleButton customFunc={onClose} text={_locate('ppp_form_annulla')} />
          <LoadingButton isLoading={saving} customFunc={handleSubmit} text={_locate('ppp_proroga_conferma')} />
        </div>
      </div>
    </div>
  );
}

function AlertModal({ scadenza, procedimentoId, onClose, onSuccess, _locate }) {
  const api = usePppApi();
  const [giorni, setGiorni] = useState(scadenza.alertGiorniPrima ?? 15);
  const [saving, setSaving] = useState(false);

  const handleSubmit = () => {
    const v = Number(giorni);
    if (!v || v < 1 || v > 90) return;
    setSaving(true);
    api.aggiornaAlert(procedimentoId, scadenza.id, { alertGiorniPrima: v }, {
      onSuccess: () => { setSaving(false); onSuccess(); onClose(); },
      onError:   () => setSaving(false),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">{_locate('ppp_alert_titolo')}</h3>
        <p className="text-xs text-gray-500 mb-4">{scadenza.descrizione}</p>

        <label className="block text-sm font-medium text-gray-700 mb-1">{_locate('ppp_alert_giorni_label')}</label>
        <input
          type="number" min={1} max={90} value={giorni}
          onChange={(e) => setGiorni(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mb-4">{_locate('ppp_alert_giorni_hint')}</p>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
            {_locate('ppp_form_annulla')}
          </button>
          <button onClick={handleSubmit} disabled={saving} className="text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? _locate('ppp_caricamento') : _locate('ppp_salva')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Vista dettaglio a schermo intero dentro il contenitore
function ScadenzaDetail({ scadenza, procedimentoId, onBack, onUpdate, canGestisci, _locate }) {
  const api = usePppApi();
  const { request } = useHttp();
  const [showProroga, setShowProroga] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [chiudendo, setChiudendo] = useState(false);
  const [downloadingProroga, setDownloadingProroga] = useState(null);

  const handleDownloadAllegato = (proroga) => {
    setDownloadingProroga(proroga.id);
    request(api.downloadAllegatoProroga(procedimentoId, scadenza.id, proroga.id), {
      method: 'GET',
      download: true,
      onSuccess: async (response) => {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = proroga.allegatoNome ?? 'allegato';
        a.click();
        URL.revokeObjectURL(url);
        setDownloadingProroga(null);
      },
      onError: () => setDownloadingProroga(null),
    });
  };

  const cfg = STATO_CONFIG[scadenza.statoVisivo] ?? STATO_CONFIG.in_tempo;
  const dataEffettiva = scadenza.dataNuovaScadenza ?? scadenza.dataScadenza;

  const badgeLabel = cfg.labelKey
    ? _locate(cfg.labelKey)
    : scadenza.giorniRimanenti < 0
      ? _locate('ppp_scadenza_scaduta')
      : `${scadenza.giorniRimanenti} ${_locate('ppp_scadenza_giorni')}`;

  const isGestibile = canGestisci
    && scadenza.stato !== 'rispettata'
    && scadenza.stato !== 'scaduta';

  const handleChiudi = () => {
    setChiudendo(true);
    api.chiudiScadenza(procedimentoId, scadenza.id, {
      onSuccess: () => { setChiudendo(false); onUpdate(); onBack(); },
      onError:   () => setChiudendo(false),
    });
  };

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className={`${cfg.bg} border-b border-gray-200 px-4 py-3 flex items-center gap-3`}>
          <button onClick={onBack} className="shrink-0 text-xs text-gray-500 hover:text-gray-800 transition-colors">
            ← {_locate('ppp_scadenza_torna_lista')}
          </button>
          <span className="text-gray-300">|</span>
          <p className="text-sm font-medium text-gray-900 leading-tight flex-1 min-w-0 truncate">{scadenza.descrizione}</p>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${cfg.badge}`}>{badgeLabel}</span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">

          {/* Date */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <span className="text-gray-400">Riferimento <span className="text-gray-700 font-medium">{fmt(scadenza.dataRiferimento)}</span></span>
            <span className="text-gray-400">Scadenza originale <span className="text-gray-700 font-medium">{fmt(scadenza.dataScadenza)}</span></span>
            {scadenza.dataNuovaScadenza && (
              <span className="text-orange-500">Prorogata al <span className="font-medium">{fmt(scadenza.dataNuovaScadenza)}</span></span>
            )}
            {scadenza.dataRispettata && (
              <span className="text-green-600">Rispettata il <span className="font-medium">{fmt(scadenza.dataRispettata)}</span></span>
            )}
            <span className="text-gray-400">Termine legale <span className="text-gray-700 font-medium">{scadenza.termineLegaleGiorni}gg</span></span>
            <span className="text-gray-400">Alert <span className="text-gray-700 font-medium">{scadenza.alertGiorniPrima}gg prima</span></span>
          </div>

          {/* Storico proroghe */}
          {scadenza.storicoProroghe?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                {_locate('ppp_proroga_storico')} ({scadenza.storicoProroghe.length})
              </p>
              <div className="space-y-1.5">
                {scadenza.storicoProroghe.map((p, i) => (
                  <div key={i} className="text-xs bg-orange-50 border border-orange-100 rounded px-3 py-2">
                    <div className="flex items-start gap-3">
                      <span className="text-gray-500 whitespace-nowrap shrink-0">
                        {fmt(p.dataPrecedente)} → <span className="font-medium text-gray-800">{fmt(p.dataNuova)}</span>
                        <span className="ml-1.5 bg-orange-200 text-orange-800 px-1 py-0.5 rounded text-[10px] font-bold">+{p.giorniProroga}gg</span>
                      </span>
                      <span className="text-gray-600 italic min-w-0">{p.motivazione}</span>
                    </div>
                    {p.hasAllegato && (
                      <button
                        onClick={() => handleDownloadAllegato(p)}
                        disabled={downloadingProroga === p.id}
                        className="mt-1.5 inline-flex items-center gap-1 text-blue-600 hover:underline text-[11px] disabled:opacity-50"
                      >
                        📎 {downloadingProroga === p.id ? '...' : (p.allegatoNome ?? 'Allegato')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Azioni */}
          {isGestibile && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setShowProroga(true)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                {_locate('ppp_proroga_azione')}
              </button>
              <button onClick={handleChiudi} disabled={chiudendo} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors">
                {chiudendo ? _locate('ppp_caricamento') : _locate('ppp_scadenza_chiudi')}
              </button>
              <button onClick={() => setShowAlert(true)} className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                {_locate('ppp_alert_azione')} ({scadenza.alertGiorniPrima ?? 15}gg)
              </button>
            </div>
          )}
        </div>
      </div>

      {showProroga && (
        <ProrogaModal
          scadenza={scadenza} procedimentoId={procedimentoId}
          onClose={() => setShowProroga(false)} onSuccess={() => { onUpdate(); onBack(); }}
          _locate={_locate}
        />
      )}
      {showAlert && (
        <AlertModal
          scadenza={scadenza} procedimentoId={procedimentoId}
          onClose={() => setShowAlert(false)} onSuccess={onUpdate}
          _locate={_locate}
        />
      )}
    </>
  );
}

function ScadenzaCard({ scadenza, onSelect, _locate }) {
  const cfg = STATO_CONFIG[scadenza.statoVisivo] ?? STATO_CONFIG.in_tempo;

  const badgeLabel = cfg.labelKey
    ? _locate(cfg.labelKey)
    : scadenza.giorniRimanenti < 0
      ? _locate('ppp_scadenza_scaduta')
      : `${scadenza.giorniRimanenti} ${_locate('ppp_scadenza_giorni')}`;

  const dataEffettiva = scadenza.dataNuovaScadenza ?? scadenza.dataScadenza;

  return (
    <button
      onClick={() => onSelect(scadenza)}
      className={`w-full text-left rounded-lg border-l-4 ${cfg.border} ${cfg.bg} p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-gray-800 leading-tight">{scadenza.descrizione}</p>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${cfg.badge}`}>
          {badgeLabel}
        </span>
      </div>

      {dataEffettiva && (
        <p className="mt-1.5 text-xs text-gray-500">
          {scadenza.dataNuovaScadenza ? _locate('ppp_scadenza_prorogata_al') : _locate('ppp_scadenza_scade_il')}{' '}
          {new Date(dataEffettiva).toLocaleDateString('it-IT')}
        </p>
      )}

      {scadenza.storicoProroghe?.length > 0 && (
        <p className="mt-1 text-xs text-gray-400">
          {scadenza.storicoProroghe.length} {_locate('ppp_proroga_storico').toLowerCase()}
        </p>
      )}

      {scadenza.riferimentoNormativo && (
        <p className="mt-1 text-xs text-gray-400 italic truncate">{scadenza.riferimentoNormativo}</p>
      )}
    </button>
  );
}

export default function ScadenzeWidget({ scadenze = [], procedimentoId, onUpdate, canGestisci = false, _locate }) {
  const [selected, setSelected] = useState(null);

  if (scadenze.length === 0) {
    return <p className="p-4 text-gray-400 text-sm">{_locate('ppp_scadenze_vuote')}</p>;
  }

  // Se c'è una scadenza selezionata mostra la vista dettaglio full-width
  if (selected) {
    // Aggiorna l'oggetto selezionato se i dati sono cambiati dopo onUpdate
    const fresh = scadenze.find((s) => s.id === selected.id) ?? selected;
    return (
      <div className="min-h-[360px]">
        <ScadenzaDetail
          scadenza={fresh}
          procedimentoId={procedimentoId}
          onBack={() => setSelected(null)}
          onUpdate={onUpdate}
          canGestisci={canGestisci}
          _locate={_locate}
        />
      </div>
    );
  }

  const attive     = scadenze.filter((s) => s.stato !== 'rispettata');
  const rispettate = scadenze.filter((s) => s.stato === 'rispettata');

  return (
    <div className="p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {attive.map((s) => (
          <ScadenzaCard key={s.id} scadenza={s} onSelect={setSelected} _locate={_locate} />
        ))}
      </div>

      {rispettate.length > 0 && (
        <details className="mt-4">
          <summary className="text-sm text-gray-500 cursor-pointer select-none">
            {_locate('ppp_scadenze_rispettate')} ({rispettate.length})
          </summary>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rispettate.map((s) => (
              <ScadenzaCard key={s.id} scadenza={s} onSelect={setSelected} _locate={_locate} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
